<template>
    <CompassTableRow
        :class="{ 'ocr-verification-products-table__row--not-expandable': !canExpand }"
        :expanded="isExpanded"
        @update:expanded="(expanded: boolean) => emit('toggle-expand', props.index, expanded)"
    >
        <CompassTableBodyCell maxWidth="200px" minWidth="200px">
            <CompassFormItem label="" :error="descriptionError">
                <CompassInput
                    v-model="description"
                    class="ocr-verification-products-table__input"
                    :disabled="props.disabled"
                    :error="!!descriptionError"
                />
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="props.isLineItemsEnabled" maxWidth="80px" minWidth="80px">
            <CompassInput
                v-model="quantity"
                class="ocr-verification-products-table__input"
                :disabled="props.disabled"
                placeholder="-"
                :type="CompassEnums.InputType.NUMBER"
            />
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassFormItem label="" :error="priceWithoutTaxError">
                <CompassInput
                    v-model="priceWithoutTax"
                    class="ocr-verification-products-table__input"
                    :disabled="props.disabled"
                    :error="!!priceWithoutTaxError"
                    inputmode="decimal"
                    @update:modelValue="onPriceInput"
                    @blur="formatPriceOnBlur"
                >
                    <template v-if="props.currencySymbol" #suffix>
                        {{ props.currencySymbol }}
                    </template>
                </CompassInput>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="110px" minWidth="110px">
            <CompassFormItem label="" :error="taxRateIdError">
                <CompassSelect
                    v-model="taxRateId"
                    class="ocr-verification-products-table__input"
                    :disabled="props.disabled"
                    filterable
                    :placeholder="$t.__('ocr.OcrVerificationForm.select_rate')"
                    :error="!!taxRateIdError"
                >
                    <CompassDropdownOption
                        v-for="item in compliantTaxRates"
                        :key="item.id"
                        :label="taxLabel(item)"
                        :value="item.id"
                    />
                </CompassSelect>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="props.isLineItemsEnabled" maxWidth="140px" minWidth="140px">
            <CompassSelect
                v-model="unit"
                class="ocr-verification-products-table__input"
                :disabled="props.disabled"
                filterable
                :placeholder="$t.__('ocr.OcrVerificationProductsTable.select_unit')"
            >
                <CompassDropdownOption :value="null" :label="$t.__('common.labels.none')" />
                <CompassDropdownOption
                    v-for="unitItem in props.units"
                    :key="unitItem.id"
                    :label="unitItem.label"
                    :value="unitItem.id"
                />
            </CompassSelect>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassInput
                class="ocr-verification-products-table__input"
                :modelValue="taxAmount"
                :disabled="lockedByEinvoicing"
                inputmode="decimal"
                @update:modelValue="onTaxAmountInput"
                @blur="formatTaxAmountOverrideOnBlur"
            >
                <template v-if="props.currencySymbol" #suffix>
                    {{ props.currencySymbol }}
                </template>
            </CompassInput>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassInput
                class="ocr-verification-products-table__input"
                :modelValue="amountIncludingTax"
                :disabled="lockedByEinvoicing"
                inputmode="decimal"
                @update:modelValue="onAmountIncludingTaxInput"
                @blur="formatAmountIncludingTaxOverrideOnBlur"
            >
                <template v-if="props.currencySymbol" #suffix>
                    {{ props.currencySymbol }}
                </template>
            </CompassInput>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="props.isLineItemsEnabled" maxWidth="260px" minWidth="260px">
            <div class="ocr-verification-products-table__catalogue-cell">
                <CompassAutoComplete
                    :key="catalogueLinkSelectKey"
                    v-model="catalogueLink"
                    class="ocr-verification-products-table__input"
                    :defaultValues="catalogueLinkDefaultValues"
                    :disabled="props.disabled"
                    :results="catalogueResults"
                    :loading="isSearchingCatalogue"
                    :triggerAfter="0"
                    menuClass="ocr-form__select-fit-dropdown"
                    :placeholder="$t.__('common.actions.search')"
                    :noResultsLabel="$t.__('common.labels.no_result')"
                    :actions="isSearchingCatalogue ? [] : catalogueActions"
                    @update-search="handleCatalogueSearch"
                    @update:modelValue="
                        (value: OcrProduct['catalogueLink']) =>
                            emit('catalogue-link-update', value, props.index)
                    "
                    @focus="handleCatalogueFocus"
                />
                <CompassTooltip
                    :content="$t.__('ocr.OcrVerificationProductsTable.update_product')"
                    :disabled="!catalogueLink"
                >
                    <CompassIconButton
                        icon="edit"
                        :disabled="props.disabled"
                        :size="CompassEnums.IconButtonSize.MEDIUM"
                        :class="{
                            'ocr-verification-products-table__catalogue-cell__edit--hidden':
                                !catalogueLink,
                        }"
                        @click="onEdit"
                    />
                </CompassTooltip>
            </div>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="200px" minWidth="200px">
            <CompassFormItem label="" :error="accountingCodeError">
                <CompassSelect
                    v-model="accountingCode"
                    class="ocr-verification-products-table__input"
                    :disabled="props.disabled"
                    filterable
                    :placeholder="$t.__('ocr.OcrVerificationForm.select_code')"
                    :error="!!accountingCodeError"
                    @update:modelValue="
                        (value: number | null) => emit('accounting-code-update', value, props.index)
                    "
                >
                    <CompassDropdownOption :value="null" :label="$t.__('common.labels.none')" />
                    <CompassDropdownOption
                        v-for="item in props.accountingCodes"
                        :key="item.id"
                        :label="`${item.code} - ${item.label}`"
                        :value="item.id"
                    />
                </CompassSelect>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="showAnalyticCodeColumn" maxWidth="200px" minWidth="200px">
            <CompassInput
                v-model="analyticCode"
                class="ocr-verification-products-table__input"
                :disabled="props.disabled"
                :placeholder="$t.__('ocr.OcrVerificationProductsTable.analytic_code')"
            />
        </CompassTableBodyCell>
        <CompassTableBodyCell idColumn="delete">
            <CompassTooltip :content="$t.__('common.actions.duplicate')" :arrow="false">
                <CompassIconButton
                    icon="duplicate"
                    :disabled="lockedByEinvoicing"
                    :size="CompassEnums.IconButtonSize.MEDIUM"
                    @click="emit('request-duplicate', props.index)"
                />
            </CompassTooltip>
            <CompassTooltip
                v-if="canDelete"
                :content="$t.__('common.actions.delete')"
                :arrow="false"
            >
                <CompassIconButton
                    icon="bin"
                    :disabled="lockedByEinvoicing"
                    :variant="CompassEnums.IconButtonVariant.DESTRUCTIVE"
                    :size="CompassEnums.IconButtonSize.MEDIUM"
                    @click="emit('request-delete', props.index)"
                />
            </CompassTooltip>
        </CompassTableBodyCell>
    </CompassTableRow>
    <CompassTableRow :visible="isExpanded && canExpand" subrow>
        <template #subrow>
            <CompassTable displayEmptyExpandableColumn>
                <CompassTableBody>
                    <CompassTableRow>
                        <CompassTableBodyCell>
                            <div class="ocr-verification-products-table__details">
                                <div class="ocr-verification-products-table__details__top">
                                    <CompassFormItem
                                        :label="$t.__('ocr.OcrVerificationForm.stock_entry_date')"
                                    >
                                        <CompassDatePicker
                                            :modelValue="stockOutDate ?? ''"
                                            :disabled="props.disabled"
                                            :formatDateDisplayed="props.formatDateDisplayed"
                                            @update:modelValue="
                                                (value: string) => (stockOutDate = value)
                                            "
                                        />
                                    </CompassFormItem>
                                    <CompassFormItem
                                        :label="$t.__('ocr.OcrVerificationForm.warehouse')"
                                    >
                                        <CompassSelect
                                            v-model="warehouseId"
                                            :disabled="props.disabled"
                                            filterable
                                            placeholder="-"
                                        >
                                            <CompassDropdownOption label="-" value="" />
                                            <CompassDropdownOption
                                                v-for="warehouse in props.warehouses"
                                                :key="warehouse.id"
                                                :label="warehouse.label"
                                                :value="warehouse.id"
                                            />
                                        </CompassSelect>
                                    </CompassFormItem>
                                </div>
                                <div v-if="isSerializedStock">
                                    <CompassFormItem
                                        :label="$t.__('ocr.OcrVerificationForm.serial_numbers')"
                                    >
                                        <CompassInput
                                            v-model="serialNumbers"
                                            :type="CompassEnums.InputType.TEXTAREA"
                                            :disabled="props.disabled"
                                            :placeholder="
                                                $t.__('ocr.OcrVerificationForm.serial_numbers')
                                            "
                                        />
                                        <span class="typographies-paragraph-small-regular">
                                            {{
                                                $t.__(
                                                    'ocr.OcrVerificationForm.stock.serialized.info',
                                                )
                                            }}
                                        </span>
                                    </CompassFormItem>
                                </div>
                            </div>
                        </CompassTableBodyCell>
                    </CompassTableRow>
                </CompassTableBody>
            </CompassTable>
        </template>
    </CompassTableRow>
</template>

<script setup lang="ts">
import {
    computed,
    inject,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
    type ComputedRef,
    type Ref,
} from 'vue';
import { useField, useSubmitCount } from 'vee-validate';
import { search } from 'api/searchEngineService';
import {
    limitToTwoDecimals,
    PRECISION_FACTOR,
    AMOUNT_DECIMAL_PLACES,
} from 'composables/useOcrProducts';
import { useTax } from 'composables/useTax';
import Item from 'api/item';
import type { OcrProduct } from 'composables/ocr/ocrFormSchema';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Warehouse } from 'types/entities/stock/warehouse.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';
import {
    CatalogueItemEvent,
    type CatalogueItemUpdatedPayload,
} from 'types/events/catalogueItem.events';

declare global {
    interface Window {
        catalogueItem: {
            edit: (
                type: string,
                id: number | undefined,
                arg3: boolean,
                arg4: string,
                options: { preventRelocate: boolean; hideAddAnother: boolean },
            ) => void;
        };
    }
}

interface Props {
    index: number;
    disabled?: boolean;
    canExpand: boolean;
    isExpanded: boolean;
    canDelete: boolean;
    taxRates: TaxRate[];
    accountingCodes: AccountingCode[];
    units?: Unit[];
    warehouses?: Warehouse[];
    showAnalyticCode?: boolean;
    isLineItemsEnabled?: boolean;
    currencySymbol?: string;
    formatDateDisplayed?: string;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    units: () => [],
    warehouses: () => [],
    showAnalyticCode: false,
    isLineItemsEnabled: false,
    currencySymbol: undefined,
    formatDateDisplayed: undefined,
});

const emit = defineEmits<{
    (event: 'accounting-code-update', value: number | null, index: number): void;
    (event: 'catalogue-link-update', value: OcrProduct['catalogueLink'], index: number): void;
    (event: 'toggle-expand', index: number, expanded?: boolean): void;
    (event: 'request-delete', index: number): void;
    (event: 'request-duplicate', index: number): void;
    (
        event: 'product-updated',
        itemId: number,
        name: string,
        accountingPurchaseCodeId: number | null,
    ): void;
}>();

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;
const { taxLabel, isEinvoicingCompliant } = useTax();

const submitCount = useSubmitCount();
const fieldOptions = { validateOnValueUpdate: false };
const fieldPath = (name: string) => (): string => `products[${props.index}].${name}`;

// Errors are only displayed once the user has attempted to submit at least once.
const gateError = (message: Ref<string | undefined>): ComputedRef<string> =>
    computed(() => (submitCount.value > 0 ? (message.value ?? '') : ''));

const { value: description, errorMessage: descriptionMessage } = useField<string>(
    fieldPath('description'),
    undefined,
    fieldOptions,
);
const descriptionError = gateError(descriptionMessage);

const { value: quantity } = useField<number | string | null>(
    fieldPath('quantity'),
    undefined,
    fieldOptions,
);
const { value: priceWithoutTax, errorMessage: priceWithoutTaxMessage } = useField<
    number | string | null
>(fieldPath('priceWithoutTax'), undefined, fieldOptions);
const priceWithoutTaxError = gateError(priceWithoutTaxMessage);

const { value: taxRateId, errorMessage: taxRateIdMessage } = useField<number | null>(
    fieldPath('taxRateId'),
    undefined,
    fieldOptions,
);
const taxRateIdError = gateError(taxRateIdMessage);
const { value: unit } = useField<string | number | null>(
    fieldPath('unit'),
    undefined,
    fieldOptions,
);
const { value: stockOutDate } = useField<string | null>(
    fieldPath('stockOutDate'),
    undefined,
    fieldOptions,
);
const { value: warehouseId } = useField<string | number | null>(
    fieldPath('warehouseId'),
    undefined,
    fieldOptions,
);
const { value: serialNumbers } = useField<string | null>(
    fieldPath('serialNumbers'),
    undefined,
    fieldOptions,
);
const { value: taxAmountOverride } = useField<number | string | null>(
    fieldPath('taxAmountOverride'),
    undefined,
    fieldOptions,
);
const { value: amountIncludingTaxOverride } = useField<number | string | null>(
    fieldPath('amountIncludingTaxOverride'),
    undefined,
    fieldOptions,
);
const { value: accountingCode, errorMessage: accountingCodeMessage } = useField<number | null>(
    fieldPath('accountingCode'),
    undefined,
    fieldOptions,
);
const accountingCodeError = gateError(accountingCodeMessage);
const { value: analyticCode } = useField<string | null>(
    fieldPath('analyticCode'),
    undefined,
    fieldOptions,
);
const { value: catalogueLink } = useField<OcrProduct['catalogueLink']>(
    fieldPath('catalogueLink'),
    undefined,
    fieldOptions,
);
const { value: related } = useField<OcrProduct['related']>(
    fieldPath('related'),
    undefined,
    fieldOptions,
);

const compliantTaxRates = computed<TaxRate[]>(() =>
    props.taxRates.filter((tax) => isEinvoicingCompliant(tax)),
);
const showAnalyticCodeColumn = computed<boolean>(
    () => Boolean(props.showAnalyticCode) && Boolean(props.isLineItemsEnabled),
);

const totalWithoutTaxInCents = computed<number | null>(() => {
    const price = parseFloat(String(priceWithoutTax.value ?? '').replace(',', '.'));
    const qty = parseFloat(String(quantity.value ?? '')) || 1;
    if (Number.isNaN(price)) {
        return null;
    }
    return Math.round(price * qty * PRECISION_FACTOR);
});

const productTaxInCents = computed<number | null>(() => {
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const taxItem = props.taxRates.find((item) => item.id === taxRateId.value);
    if (withoutTaxInCents === null || !taxItem) {
        return null;
    }
    return Math.round((withoutTaxInCents * Number(taxItem.rate)) / 100);
});

const computedTaxAmount = computed<string | null>(() => {
    if (productTaxInCents.value === null) {
        return null;
    }
    return (productTaxInCents.value / PRECISION_FACTOR)
        .toFixed(AMOUNT_DECIMAL_PLACES)
        .replace('.', ',');
});

const computedAmountIncludingTax = computed<string | null>(() => {
    if (productTaxInCents.value === null || totalWithoutTaxInCents.value === null) {
        return null;
    }
    return ((totalWithoutTaxInCents.value + productTaxInCents.value) / PRECISION_FACTOR)
        .toFixed(AMOUNT_DECIMAL_PLACES)
        .replace('.', ',');
});

const taxAmount = computed<number | string | null>(
    () => taxAmountOverride.value ?? computedTaxAmount.value,
);
const amountIncludingTax = computed<number | string | null>(
    () => amountIncludingTaxOverride.value ?? computedAmountIncludingTax.value,
);

const isSerializedStock = computed<boolean>(() => related.value?.serialized_stock === 'Y');

watch([priceWithoutTax, quantity, taxRateId], () => {
    taxAmountOverride.value = null;
    amountIncludingTaxOverride.value = null;
});

const onPriceInput = (value: string | number | null): void => {
    priceWithoutTax.value = limitToTwoDecimals(value);
};

const formatRefToTwoDecimals = (fieldRef: Ref<number | string | null>): void => {
    const value = fieldRef.value;
    if (value === null || value === '') {
        return;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!Number.isNaN(parsed)) {
        fieldRef.value = parsed.toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ',');
    }
};

const formatPriceOnBlur = (): void => formatRefToTwoDecimals(priceWithoutTax);
const formatTaxAmountOverrideOnBlur = (): void => formatRefToTwoDecimals(taxAmountOverride);
const formatAmountIncludingTaxOverrideOnBlur = (): void =>
    formatRefToTwoDecimals(amountIncludingTaxOverride);

const formatCentsAsAmount = (cents: number): string =>
    (cents / PRECISION_FACTOR).toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ',');

const parseAmountToCents = (value: string | number | null): number | null => {
    const parsed = parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isNaN(parsed) ? null : Math.round(parsed * PRECISION_FACTOR);
};

const onTaxAmountInput = (value: string | number | null): void => {
    const limited = limitToTwoDecimals(value);
    taxAmountOverride.value = limited;
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const taxInCents = parseAmountToCents(limited);
    if (withoutTaxInCents !== null && taxInCents !== null) {
        amountIncludingTaxOverride.value = formatCentsAsAmount(withoutTaxInCents + taxInCents);
    }
};

const onAmountIncludingTaxInput = (value: string | number | null): void => {
    const limited = limitToTwoDecimals(value);
    amountIncludingTaxOverride.value = limited;
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const totalWithTaxInCents = parseAmountToCents(limited);
    if (withoutTaxInCents !== null && totalWithTaxInCents !== null) {
        taxAmountOverride.value = formatCentsAsAmount(totalWithTaxInCents - withoutTaxInCents);
    }
};

const catalogueLinkDefaultValues = computed<{ value: number; label: string }[]>(() => {
    const link = catalogueLink.value;
    return link?.id ? [{ value: link.id, label: link.label ?? '' }] : [];
});

const catalogueLinkSelectKey = ref<number>(0);
const catalogueResults = ref<
    { label: string; id: number; value: number; itemType: string; icon: string }[]
>([]);
const isSearchingCatalogue = ref<boolean>(false);
let catalogueSearchAbortController: AbortController | null = null;

const abortCatalogueSearch = (): void => {
    if (catalogueSearchAbortController) {
        catalogueSearchAbortController.abort();
        catalogueSearchAbortController = null;
    }
};

const handleCatalogueFocus = (): void => {
    catalogueResults.value = [...catalogueResults.value];
};

const handleCatalogueSearch = async (query: string): Promise<void> => {
    if (!query) {
        abortCatalogueSearch();
        catalogueResults.value = [];
        isSearchingCatalogue.value = false;
        return;
    }

    abortCatalogueSearch();

    const controller = new AbortController();
    catalogueSearchAbortController = controller;

    isSearchingCatalogue.value = true;
    await nextTick();

    if (catalogueSearchAbortController !== controller) {
        return;
    }

    catalogueResults.value = [];

    try {
        const searchResponse = (await search({
            query,
            type: ['item.product', 'item.service'],
            signal: controller.signal,
        })) as { data: { data?: SearchResult[] } };
        const data = searchResponse.data.data;

        if (catalogueSearchAbortController !== controller) {
            return;
        }

        catalogueResults.value = (data ?? []).map((result: SearchResult) => ({
            label: result.name || result.number || '',
            id: result.object.id,
            value: result.object.id,
            itemType: result.object.type === 'item.service' ? 'service' : 'item',
            icon: 'obj-slsy-product',
        }));
    } catch {
        if (catalogueSearchAbortController !== controller) {
            return;
        }
        catalogueResults.value = [];
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('ocr.OcrVerificationForm.error_generic'),
            duration: 5000,
        });
    } finally {
        if (catalogueSearchAbortController === controller) {
            catalogueSearchAbortController = null;
            isSearchingCatalogue.value = false;
        }
    }
};

interface SearchResult {
    name?: string;
    number?: string;
    object: { id: number; type: string };
}

interface ItemApiData {
    name: string;
    accounting_purchase_code_id: number | null;
    type: string;
}

interface ItemApiResponse {
    data: ItemApiData;
}

const getUpdatedProductData = async (
    itemId: number,
): Promise<{
    name: string;
    accountingPurchaseCodeId: number | null;
    itemType: 'item' | 'service';
}> => {
    const { data } = (await Item.getItemById(itemId)) as ItemApiResponse;
    const itemType: 'item' | 'service' = data.type === 'service' ? 'service' : 'item';
    return {
        name: data.name,
        accountingPurchaseCodeId: data.accounting_purchase_code_id,
        itemType,
    };
};

type CatalogueItemUpdateHandler = (payload: CatalogueItemUpdatedPayload) => void;
let currentItemUpdateHandler: CatalogueItemUpdateHandler | null = null;

const cleanupItemUpdateHandler = (): void => {
    if (currentItemUpdateHandler) {
        EventBus.off(CatalogueItemEvent.UPDATED, currentItemUpdateHandler);
        currentItemUpdateHandler = null;
    }
};

const createCatalogueItem = (type: string): void => {
    cleanupItemUpdateHandler();

    const handleItemCreated: CatalogueItemUpdateHandler = (payload) => {
        const itemId = payload.item?.id;
        if (!itemId) {
            cleanupItemUpdateHandler();
            return;
        }

        void (async () => {
            try {
                const { name, itemType } = await getUpdatedProductData(itemId);
                const newLink: NonNullable<OcrProduct['catalogueLink']> = {
                    id: itemId,
                    label: name,
                    value: itemId,
                    itemType,
                };
                catalogueLink.value = newLink;
                emit('catalogue-link-update', newLink, props.index);
                catalogueLinkSelectKey.value++;
            } catch {
                $pushToast({
                    type: CompassEnums.ToastType.ERROR,
                    icon: true,
                    text: $t.__('ocr.OcrVerificationForm.error_generic'),
                    duration: 5000,
                });
            } finally {
                cleanupItemUpdateHandler();
            }
        })();
    };

    currentItemUpdateHandler = handleItemCreated;
    EventBus.on(CatalogueItemEvent.UPDATED, handleItemCreated);

    window.catalogueItem.edit(type, undefined, false, '', {
        preventRelocate: true,
        hideAddAnother: true,
    });
};

const catalogueActions: { label: string; callback: () => void }[] = [
    {
        label: $t.__('ocr.OcrVerificationProductsTable.add_product'),
        callback: () => createCatalogueItem('item'),
    },
    {
        label: $t.__('ocr.OcrVerificationProductsTable.add_service'),
        callback: () => createCatalogueItem('service'),
    },
];

const onEdit = (): void => {
    const itemId = catalogueLink.value?.id;
    if (!itemId) {
        return;
    }

    cleanupItemUpdateHandler();

    const handleItemUpdate: CatalogueItemUpdateHandler = () => {
        void (async () => {
            try {
                const { name, accountingPurchaseCodeId } = await getUpdatedProductData(itemId);
                if (name) {
                    emit('product-updated', itemId, name, accountingPurchaseCodeId);
                }
            } catch {
                $pushToast({
                    type: CompassEnums.ToastType.ERROR,
                    icon: true,
                    text: $t.__('ocr.OcrVerificationForm.error_generic'),
                    duration: 5000,
                });
            } finally {
                cleanupItemUpdateHandler();
            }
        })();
    };

    currentItemUpdateHandler = handleItemUpdate;
    EventBus.on(CatalogueItemEvent.UPDATED, handleItemUpdate);

    const type = catalogueLink.value?.itemType ?? 'item';
    window.catalogueItem.edit(type, itemId, false, '', {
        preventRelocate: true,
        hideAddAnother: true,
    });
};

onBeforeUnmount(() => {
    abortCatalogueSearch();
    cleanupItemUpdateHandler();
});
</script>

<style lang="scss" scoped>
:deep(.compass-table__cell-container) {
    width: 100%;
}

.ocr-verification-products-table {
    &__catalogue-cell {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        width: 100%;

        &__edit--hidden {
            visibility: hidden;
            pointer-events: none;
        }

        .ocr-verification-products-table__input {
            flex: 1;
            min-width: 0;
        }
    }
    &__input {
        width: 100%;
    }

    &__details {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        &__top {
            display: flex;
            gap: var(--spacing-3);
        }
        &__row--not-expandable :deep(.compass-table__row-expand-button) {
            display: none;
        }
    }
}

:deep(.compass-table__cell) {
    vertical-align: top;
}

:deep(.compass-form-item) {
    width: 100%;

    .compass-form-item__caption-container,
    .compass-form-item__caption {
        width: 100%;
    }

    .compass-form-item__caption {
        white-space: normal;
    }
}
</style>
